import { localToday } from '../../lib/flightFilters';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { addDoc, collection, getDocs, limit, onSnapshot, orderBy, query, serverTimestamp, Timestamp, updateDoc, where } from 'firebase/firestore';
import { fetchFlightTrack } from '../../lib/flightTrack';
import { db } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { DEMO_AIRCRAFT, DEMO_BOOKINGS, DEMO_FLIGHTS, DEMO_LESSEES, DEMO_SQUAWKS } from '../../lib/demoData';
import type { FlightLogDoc } from '../../lib/types';
import { computeFlightCharge } from '../../lib/billing';
import { projectFlightCharge, promoRateFor } from '../../lib/billingCycles';
import { mxStatus } from '../../lib/maintenance';
import { sendAlert } from '../../lib/alerts';
import { buildFuelMessage, buildFuelSmsHref, buildFuelCallHref, FUEL_FBO, FUEL_PARKING } from '../../lib/fuelRequest';
import type { AircraftDoc, BookingDoc, FlightDoc, MaintenanceItemDoc, SquawkDoc, UserDoc } from '../../lib/types';
import { localDatePart } from '../../lib/format';
import { ChevronDown, Fuel } from 'lucide-react';
import { PageHeader } from '../../components/PageHeader';
import { StatusBanner } from '../../components/StatusBanner';
import { DateField } from '../../components/DateField';
import { ModalClose } from '../../components/ModalClose';
import { OWNER_FIRST } from '../../lib/identity';


import { TRACKED_REG } from '../../lib/flightTrack';
export function LogFlight() {
  const { profile, demoMode, isAdmin } = useAuth();
  const [searchParams] = useSearchParams();

  // ADMIN "LOG FOR LESSEE": the owner can log a flight ON BEHALF of a lessee who
  // forgot to log it (?for=<uid> preselects them). The flight is written under the
  // lessee's uid, so onFlightLogged bills THEIR card exactly as if they'd logged
  // it themselves. The doc is stamped loggedByUid/Name for the audit trail.
  const [lessees, setLessees] = useState<UserDoc[]>([]);
  useEffect(() => {
    if (demoMode || !db || !isAdmin) return;
    const unsub = onSnapshot(query(collection(db, 'users'), where('role', '==', 'lessee')), (snap) =>
      setLessees(snap.docs.map((d) => d.data() as UserDoc))
    );
    return () => unsub();
  }, [demoMode, isAdmin]);
  const [subjectUid, setSubjectUid] = useState(searchParams.get('for') ?? '');
  const subject = (isAdmin && subjectUid && lessees.find((l) => l.uid === subjectUid)) || null;
  const adminFor = Boolean(subject);
  // Whose flight this is — drives the uid on the doc, the billing classification,
  // and the name on the shared logbook. Self unless the admin picked a lessee.
  const pilot = subject ?? profile;
  const pilotFirst = subject?.personal?.firstName || 'the pilot';

  // How this flight is billed. Admins log their own (owner) flights for free.
  // A lessee on the 'promo' plan pays a flat $75/Hobbs-hr with no monthly minimum;
  // a standard lessee pays the $1,000/10hr minimum + $100/hr overage.
  const isOwner = (isAdmin && !adminFor) || pilot?.billingProfile === 'owner';
  // A CFI ('cfi' profile, promo plan) bills like any promo pilot: their own flying
  // at the promo rate, no monthly minimum. Checkouts are no longer free (Casey,
  // 10/10/26) — a checkout is the renter's flight, logged and paid by the renter.
  const isCfi = !isOwner && pilot?.billingProfile === 'cfi';
  // Owner logging their own flight can test a discovery booking (never billed).
  const ownerTest = isAdmin && !adminFor;
  const isPromo = !isOwner && pilot?.plan === 'promo';
  // Comped plan (owner's guest, e.g. Tyson): flights recorded, never charged.
  const isComped = !isOwner && pilot?.plan === 'comped';
  const nav = useNavigate();
  const today = localToday();

  const [aircraft, setAircraft] = useState<AircraftDoc[]>([]);
  useEffect(() => {
    if (demoMode) {
      setAircraft(DEMO_AIRCRAFT);
      return;
    }
    if (!db) return;
    const unsub = onSnapshot(collection(db, 'aircraft'), (snap) =>
      setAircraft(snap.docs.map((d) => d.data() as AircraftDoc))
    );
    return () => unsub();
  }, [demoMode]);

  const ac = aircraft[0];
  const tail = ac?.tailNumber || TRACKED_REG;

  // The pilot's already-logged flights — needed to estimate this flight's
  // incremental overage (the first 10 hrs each cycle are covered by the minimum).
  const [myFlights, setMyFlights] = useState<FlightDoc[]>([]);
  useEffect(() => {
    if (demoMode || !db || !pilot?.uid) return;
    const unsub = onSnapshot(
      query(collection(db, 'flights'), where('lesseeUid', '==', pilot.uid)),
      (snap) => setMyFlights(snap.docs.map((d) => d.data() as FlightDoc))
    );
    return () => unsub();
  }, [demoMode, pilot?.uid]);

  // Airworthiness items — an overdue one that grounds the plane blocks logging.
  const [maintenance, setMaintenance] = useState<MaintenanceItemDoc[]>([]);
  // Discovery: the guest is charged only if the airplane took off (their card consent says so).
  const [tookOff, setTookOff] = useState<'' | 'yes' | 'no'>('');
  useEffect(() => {
    if (demoMode || !db) return;
    const unsub = onSnapshot(collection(db, 'maintenance'), (snap) =>
      setMaintenance(snap.docs.map((d) => ({ ...(d.data() as MaintenanceItemDoc), itemId: d.id })))
    );
    return () => unsub();
  }, [demoMode]);

  // Open grounding squawks block flight logging (real data in live mode).
  const [squawks, setSquawks] = useState<SquawkDoc[]>(demoMode ? DEMO_SQUAWKS : []);
  useEffect(() => {
    if (demoMode) {
      setSquawks(DEMO_SQUAWKS);
      return;
    }
    if (!db) return;
    const unsub = onSnapshot(collection(db, 'squawks'), (snap) =>
      setSquawks(snap.docs.map((d) => d.data() as SquawkDoc))
    );
    return () => unsub();
  }, [demoMode]);

  const [date, setDate] = useState(today);

  // CFI discovery flights: a discovery booking on this date is billed to the GUEST's card
  // (fuel included, Casey 10/26/26), never the CFI's. The CFI picks which guest when there are several;
  // "No" logs a separate flight of their own. The server re-checks the booking before charging.
  const [myBookings, setMyBookings] = useState<(BookingDoc & { docId: string })[]>([]);
  useEffect(() => {
    if (demoMode) {
      // Demo: the sample bookings (the demo CFI's discovery flight lives there).
      if (isCfi || ownerTest) setMyBookings(DEMO_BOOKINGS.filter((b) => b.lesseeUid === pilot?.uid).map((b) => ({ ...b, docId: b.bookingId })));
      return;
    }
    if (!db || !(isCfi || ownerTest) || !pilot?.uid) return;
    const unsub = onSnapshot(
      query(collection(db, 'bookings'), where('lesseeUid', '==', pilot.uid)),
      (snap) => setMyBookings(snap.docs.map((d) => ({ ...(d.data() as BookingDoc), docId: d.id })))
    );
    return () => unsub();
  }, [demoMode, isCfi, ownerTest, pilot?.uid]);
  const discoveryBookings = useMemo(
    () =>
      myBookings.filter(
        (b) =>
          b.flightPurpose === 'discovery' &&
          b.status !== 'cancelled' &&
          b.status !== 'denied' &&
          !b.discoveryFlightId && // already logged (and billed to the guest)
          b.startDateTime &&
          localDatePart(b.startDateTime.toDate()) === date
      ),
    [myBookings, date]
  );
  const [discoveryPick, setDiscoveryPick] = useState<string>(''); // booking docId, or 'none'
  useEffect(() => {
    if (discoveryPick === 'none') return;
    if (!discoveryBookings.some((b) => b.docId === discoveryPick)) setDiscoveryPick(discoveryBookings[0]?.docId ?? '');
  }, [discoveryBookings, discoveryPick]);
  const discoveryBooking = discoveryBookings.find((b) => b.docId === discoveryPick) ?? null;
  // Fuel-included discovery flight (Casey, 10/26/26): the guest's card pays the airplane, never the CFI.
  const isDiscovery = isCfi && Boolean(discoveryBooking);
  const guestRate = ac?.discoveryRate ?? 170; // $/Hobbs hr, fuel included
  const guestName = discoveryBooking?.waiverGuestName || discoveryBooking?.discoveryGuest || 'your guest';
  // The waiver gate applies to the owner's test run too.
  const discoveryGate = (isDiscovery || ownerTest) && Boolean(discoveryBooking);
  const [hobbsIn, setHobbsIn] = useState('');
  const [tachIn, setTachIn] = useState('');
  // Hobbs/tach INTEGRITY (billing-critical). The pilot never types the master
  // reading. The START of this flight is read-only, derived from the aircraft's
  // current value (ac.currentHobbs/currentTach). A required starting-reading gauge
  // check confirms those numbers match the gauge BEFORE flying. If they DON'T
  // (someone before this pilot forgot to log), the pilot enters what the gauge
  // actually reads — the OBSERVED start — and that, not the stale master,
  // becomes hobbsOut/tachOut. So they're billed only from their own start; the
  // server records a discrepancy + alerts the owner.
  const expectedHobbs = ac?.currentHobbs ?? 0;
  const expectedTach = ac?.currentTach ?? 0;
  // null = not yet checked (gate active); true = gauge matches; false = mismatch
  const [gaugeMatches, setGaugeMatches] = useState<boolean | null>(null);
  const [observedHobbs, setObservedHobbs] = useState('');
  const [observedTach, setObservedTach] = useState('');
  // The actual start readings used for billing: the observed values on a
  // mismatch, otherwise the aircraft's current master reading.
  // WHEN the master reading was last set, so a pilot (or the owner checking up)
  // can tell at a glance whether the number is from yesterday's flight or from
  // two weeks ago — a stale date next to a moved gauge means someone didn't log.
  // Source: the newest shared-logbook entry (readable by every pilot), unless the
  // owner hand-set the master more recently (readingAudit), which wins.
  const [lastLog, setLastLog] = useState<{ date: string; who: string } | null>(null);
  useEffect(() => {
    if (demoMode) {
      const f = [...DEMO_FLIGHTS].sort((a, b) => b.hobbsIn - a.hobbsIn)[0];
      const l = f && DEMO_LESSEES.find((x) => x.uid === f.lesseeUid);
      setLastLog(f ? { date: f.date, who: l?.personal?.firstName ?? 'Pilot' } : null);
      return;
    }
    if (!db) return;
    const unsub = onSnapshot(
      query(collection(db, 'flightLog'), orderBy('createdAt', 'desc'), limit(1)),
      (snap) => {
        const e = snap.docs[0]?.data() as FlightLogDoc | undefined;
        setLastLog(e ? { date: e.date, who: e.pilotName.split(' ')[0] || 'Pilot' } : null);
      },
      () => setLastLog(null)
    );
    return () => unsub();
  }, [demoMode]);
  const lastSet = useMemo(() => {
    const audit = ac?.readingAudit?.[ac.readingAudit.length - 1];
    const auditDay = audit?.at ? localDay(audit.at) : '';
    if (auditDay && (!lastLog || auditDay > lastLog.date)) return { date: auditDay, who: 'owner (manual set)' };
    return lastLog;
  }, [ac?.readingAudit, lastLog]);

  const hobbsOut = gaugeMatches === false ? observedHobbs : expectedHobbs.toFixed(1);
  const tachOut = gaugeMatches === false ? observedTach : expectedTach.toFixed(1);
  const [departureAirport, setDepartureAirport] = useState('KLGB');
  const [showDeparture, setShowDeparture] = useState(false);
  const [destinations, setDestinations] = useState('');
  const [passengerCount, setPassengerCount] = useState(0);
  const [hasSquawks, setHasSquawks] = useState(false);
  const [awayNights, setAwayNights] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  // Cash settlement (Casey, 2026-08-28). Only offered where a charge would actually
  // happen — owner, comped and CFI-checkout flights are never billed either way.
  const [payCash, setPayCash] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [fuelPrompt, setFuelPrompt] = useState(false);
  // Brief acknowledgement so dismissing the fuel step doesn't just blink away.
  const [fuelAck, setFuelAck] = useState(false);
  const [fuelTapped, setFuelTapped] = useState(false);

  const billing = useMemo(() => {
    if (!ac || !hobbsOut || !hobbsIn) return null;
    const hOut = Number(hobbsOut);
    const hIn = Number(hobbsIn);
    if (Number.isNaN(hOut) || Number.isNaN(hIn) || hIn <= hOut) return null;
    return computeFlightCharge({
      aircraft: ac,
      hobbsOut: hOut,
      hobbsIn: hIn,
      awayNights,
    });
  }, [ac, hobbsOut, hobbsIn, awayNights]);

  // The amount that will actually hit the card now: the $100/hr charge for the
  // Hobbs hours flown (no monthly minimum), plus any away-from-base charge.
  // Server is authoritative; this is the estimate.
  const est = useMemo(() => {
    if (!ac || !billing) return null;
    return projectFlightCharge({
      aircraft: ac,
      hoursFlown: billing.hoursFlown,
      awayCharge: billing.awayFromBaseCharge,
    });
  }, [ac, billing]);

  // What actually hits the card now, and how this flight is classified, by profile.
  // promo plan $/Hobbs-hr ($75)
  const promoRate = ac ? promoRateFor(ac) : 0;
  const charge = useMemo<{ amount: number; mode: NonNullable<FlightDoc['billingMode']> }>(() => {
    if (isOwner) return { amount: 0, mode: 'owner' };
    if (isComped) return { amount: 0, mode: 'comped' }; // owner's guest — never charged
    if (isDiscovery) return { amount: 0, mode: 'discovery' }; // the guest pays, on their own card
    if (isPromo)
      return {
        amount: billing ? +(billing.hoursFlown * promoRate + billing.awayFromBaseCharge).toFixed(2) : 0,
        mode: 'promo',
      };
    return { amount: est?.estCharge ?? 0, mode: 'standard' };
  }, [isOwner, isComped, isPromo, isDiscovery, billing, promoRate, est]);

  // Fuel is the pilot's own responsibility — they buy it directly from the FBO,
  // per the dry lease. (The fuel pass-through billing was removed 2026-06-07.)
  const chargedNow = +charge.amount.toFixed(2);

  // A gauge mismatch the pilot reported (observed start ≠ system's expected).
  // The pilot is billed only from their OBSERVED start; the server records a
  // discrepancy and notifies the owner to reconcile the master reading.
  const hobbsDiscrepancy =
    gaugeMatches === false && observedHobbs && Math.abs(Number(observedHobbs) - expectedHobbs) > 0.05;
  const tachDiscrepancy =
    gaugeMatches === false && observedTach && Math.abs(Number(observedTach) - expectedTach) > 0.05;

  // Sanity checks on the readings — catch typos BEFORE a card gets charged.
  const checks = useMemo(() => {
    const errors: string[] = [];
    const warnings: string[] = [];
    const hOut = Number(hobbsOut);
    const hIn = Number(hobbsIn);
    const tOut = Number(tachOut);
    const tIn = Number(tachIn);
    // end >= start is enforced (the pilot reads END off the gauge; start is the
    // confirmed/observed value). Block submit when END isn't past START.
    if (hobbsIn && hIn <= hOut) errors.push('Hobbs in must be greater than Hobbs out.');
    if (tachIn && tIn <= tOut) errors.push('Tach in must be greater than Tach out.');
    // A reported gauge mismatch is informational, not blocking — the pilot
    // proceeds on the observed reading and the owner is alerted to reconcile.
    if (hobbsDiscrepancy)
      warnings.push(
        `The gauge (${Number(observedHobbs).toFixed(1)}) doesn't match the last recorded Hobbs (${expectedHobbs.toFixed(1)}). You'll be billed only from ${Number(observedHobbs).toFixed(1)} — the owner is notified to reconcile the difference.`
      );
    if (tachDiscrepancy)
      warnings.push(
        `The gauge (${Number(observedTach).toFixed(1)}) doesn't match the last recorded tach (${expectedTach.toFixed(1)}). The owner is notified to reconcile.`
      );
    const hd = hIn - hOut;
    const td = tIn - tOut;
    if (hobbsIn && hd > 10)
      warnings.push(`That's ${hd.toFixed(1)} Hobbs hours on one flight — unusually long. Double-check the END reading.`);
    if (hobbsIn && tachIn && hd > 0 && td > 0 && Math.abs(hd - td) > 0.5 && Math.abs(hd - td) > 0.25 * hd)
      warnings.push(
        `Hobbs time (${hd.toFixed(1)}) and tach time (${td.toFixed(1)}) differ by a lot — double-check both.`
      );
    // A discovery flight needs the guest's signed waiver on the booking first.
    if (discoveryGate && discoveryBooking && !discoveryBooking.waiverId)
      errors.push(
        `${discoveryBooking.discoveryGuest || 'Your guest'} hasn’t signed the discovery flight waiver. Open the booking under My bookings and tap Guest waiver.`
      );
    else if (isDiscovery && discoveryBooking && !discoveryBooking.guestCardLast4)
      errors.push(
        `${guestName} hasn’t saved a card for the airplane. Open the booking under My bookings and tap Guest card, then hand them the phone.`
      );
    if (isDiscovery && discoveryBooking && !tookOff) errors.push('Say whether the airplane took off. The guest is only charged if it did.');
    return { errors, warnings };
  }, [hobbsOut, hobbsIn, tachOut, tachIn, hobbsDiscrepancy, tachDiscrepancy, observedHobbs, observedTach, expectedHobbs, expectedTach, discoveryGate, discoveryBooking, isDiscovery, guestName, tookOff]);

  const blockers: string[] = [];
  // Retro-logging on a lessee's behalf skips the pre-flight gates — the flight
  // already happened; the point is getting it recorded and billed.
  if (!adminFor) {
    if (profile?.status !== 'active') blockers.push(`Account status: ${profile?.status}.`);
    const logPassOpen = !!profile?.logPass?.until && profile.logPass.until.toMillis() > Date.now();
    if (!isOwner && !profile?.leaseSignedAt && !logPassOpen)
      blockers.push('Your lease hasn’t been signed yet — nothing can be flown or logged until it’s on file.');
    // No payment, no plane: block the moment a charge (the $1,000 minimum, etc.)
    // fails — not only after dunning is exhausted — per the charge-up-front policy.
    if (!isOwner && (profile?.paymentPastDue || profile?.paymentFailedFlag))
      blockers.push('Payment needed — a charge on your account didn’t go through. Update your card on the Billing page to resume flying.');
    const medExp = profile?.pilot?.medicalExpiration ? new Date(profile.pilot.medicalExpiration) : null;
    if (medExp && medExp < new Date()) blockers.push('Medical expired.');
    if (ac?.groundedByOwner)
      blockers.push(`Aircraft is grounded by the owner${ac.groundedReason ? ` — ${ac.groundedReason}` : ''}.`);
    const groundingOpen = squawks.some((s) => s.aircraftId === ac?.aircraftId && s.isGrounding && s.status !== 'resolved');
    if (groundingOpen) blockers.push('Aircraft has an open grounding squawk.');
    const mxOverdue = ac
      ? maintenance.filter((m) => m.isAirworthiness && mxStatus(m, ac.currentTach).level === 3).map((m) => m.label)
      : [];
    if (mxOverdue.length) blockers.push(`Airworthiness overdue: ${mxOverdue.join(', ')}. Contact the owner.`);
  }

  // Heads-ups when logging for a lessee — the server only bills ACTIVE accounts,
  // and a charge needs a card on file. Non-blocking: recording the flight still
  // keeps the airframe time straight even if the charge has to wait.
  const adminWarnings: string[] = [];
  if (adminFor && subject) {
    if (subject.status !== 'active' && !isComped)
      adminWarnings.push(
        `${pilotFirst}'s account is "${subject.status}" — the flight will be recorded, but the server only charges active accounts.`
      );
    if (!subject.leaseSignedAt && !isComped)
      adminWarnings.push(
        `${pilotFirst} has no signed lease on file — recording this anyway, but there is no executed agreement behind the charge.`
      );
    // A comped pilot is never charged, so a missing card doesn't matter.
    if ((!subject.stripeCustomerId || !subject.paymentMethodOnFile) && !isComped)
      adminWarnings.push(`${pilotFirst} has no card on file — the charge will fail and go to the retry/dunning flow.`);
  }

  // The gauge check must be completed before the readings/billing engage, and a
  // reported mismatch needs the observed reading filled in.
  const gaugeReady =
    gaugeMatches === true ||
    (gaugeMatches === false && Number(observedHobbs) > 0 && Number(observedTach) > 0);

  // The form's submit only opens the confirmation — nothing is charged here.
  function review(e: React.FormEvent) {
    e.preventDefault();
    if (!ac || !billing || checks.errors.length > 0 || !gaugeReady) return;
    setConfirming(true);
  }

  const flightsRoute = isAdmin ? '/admin/flights' : '/flights';
  const squawkRoute = isAdmin ? '/admin/squawks' : '/squawks/new';

  // Fuel top-off text. Built here so the pilot can read the exact wording before
  // it opens in Messages — nothing is sent or stored on our side either way.
  const fuelArgs = {
    pilotName:
      `${pilot?.personal?.firstName ?? ''} ${pilot?.personal?.lastName ?? ''}`.trim() ||
      pilot?.email ||
      '',
    tailNumber: ac?.tailNumber || TRACKED_REG,
    isOwner,
    // Discovery flights include fuel: AJM pays the top-off, so the aircraft's card is the right one.
    discovery: isDiscovery,
    demo: demoMode,
  };
  const fuelMessage = buildFuelMessage(fuelArgs);
  const fuelHref = buildFuelSmsHref(fuelArgs);

  // Runs only after the pilot confirms in the modal.
  async function doSubmit() {
    if (!ac || !profile || !pilot || !billing) return;
    setSubmitting(true);
    setResult(null);

    // NOTE: no stripeChargeId in real mode — the onFlightLogged Cloud Function
    // is the source of truth. It reconciles the cycle and charges only the real
    // overage, then writes stripeChargeId + the actual amount back to this doc.
    const flight: Omit<FlightDoc, 'stripeChargeId'> & { stripeChargeId?: string } = {
      flightId: `f-${Date.now()}`,
      aircraftId: ac.aircraftId,
      lesseeUid: pilot.uid,
      date,
      hobbsOut: Number(hobbsOut),
      hobbsIn: Number(hobbsIn),
      tachOut: Number(tachOut),
      tachIn: Number(tachIn),
      hoursFlown: billing.hoursFlown,
      departureAirport: departureAirport.toUpperCase(),
      destinations: destinations
        .split(',')
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean),
      passengerCount: discoveryGate ? 0 : passengerCount,
      baseCharge: billing.baseCharge,
      awayFromBaseCharge: billing.awayFromBaseCharge,
      totalCharge: charge.amount, // aircraft charge estimate; server is authoritative
      billingMode: charge.mode,
      ...(charge.mode === 'discovery' && discoveryBooking && { bookingId: discoveryBooking.docId, discoveryTookOff: tookOff === 'yes' }),
      ...(chargedNow > 0 && { paymentMethod: payCash ? ('cash' as const) : ('card' as const) }),
      // Owner-entered on the lessee's behalf — billing is identical, this is the audit trail.
      ...(adminFor && {
        loggedByUid: profile.uid,
        loggedByName:
          `${profile.personal?.firstName ?? ''} ${profile.personal?.lastName ?? ''}`.trim() || profile.email,
      }),
      createdAt: Timestamp.now(),
    };

    if (demoMode) {
      // No-op; in real mode the write triggers the server charge.
    } else if (db) {
      const flightRef = await addDoc(collection(db, 'flights'), { ...flight, createdAt: serverTimestamp() });
      // Shared logbook entry — non-financial, visible to every pilot. Best-effort:
      // the flight itself is already saved above, so a failure here never blocks logging.
      addDoc(collection(db, 'flightLog'), {
        flightId: flight.flightId,
        aircraftId: flight.aircraftId,
        pilotUid: pilot.uid,
        pilotName:
          `${pilot.personal?.firstName ?? ''} ${pilot.personal?.lastName ?? ''}`.trim() ||
          pilot.email ||
          'Pilot',
        date: flight.date,
        hobbsOut: flight.hobbsOut,
        hobbsIn: flight.hobbsIn,
        tachOut: flight.tachOut,
        tachIn: flight.tachIn,
        hoursFlown: flight.hoursFlown,
        departureAirport: flight.departureAirport,
        destinations: flight.destinations,
        createdAt: serverTimestamp(),
      }).catch(() => {});
      // Grab the ADS-B track now, while adsb.lol's trace still covers this flight (non-blocking).
      fetchFlightTrack(ac?.tailNumber || TRACKED_REG)
        .then((t) => t && updateDoc(flightRef, { track: t.track, trackMaxAlt: t.maxAlt }))
        .catch(() => {});
      sendAlert('flight_logged', {
        name:
          (`${pilot.personal?.firstName ?? ''} ${pilot.personal?.lastName ?? ''}`.trim() || pilot.email) +
          (adminFor ? ' (entered by owner)' : ''),
        date: flight.date,
        hobbs: `${flight.hobbsOut} → ${flight.hobbsIn}`,
        hours: flight.hoursFlown.toFixed(1),
        destinations: flight.destinations.join(', ') || '—',
        charge:
          charge.mode === 'owner'
            ? 'Owner — not billed'
            : charge.mode === 'discovery'
            ? `Guest's card, $${(flight.hoursFlown * guestRate).toFixed(2)} ($${guestRate}/hr, fuel included)`
            : chargedNow > 0
            ? `$${chargedNow.toFixed(2)}`
            : 'No charge',
      });
    }

    setConfirming(false);
    setResult(
      isOwner
        ? 'Flight logged. Owner flight — not billed. The Hobbs/Tach are recorded for the aircraft.'
        : charge.mode === 'discovery'
        ? `Flight logged. ${guestName} is being charged $${(flight.hoursFlown * guestRate).toFixed(2)} for the airplane, fuel included, and gets the receipt by email. You weren’t charged.`
        : chargedNow > 0
        ? adminFor
          ? payCash
            ? `Flight logged for ${pilotFirst}. $${chargedNow.toFixed(2)} is recorded as cash owed — no card charge.`
            : `Flight logged for ${pilotFirst}. $${chargedNow.toFixed(2)} is being charged to their card — they'll get the receipt by email.`
          : payCash
          ? `Flight logged. $${chargedNow.toFixed(2)} is recorded as cash owed to ${OWNER_FIRST} — your card was not charged.`
          : `Flight logged. $${chargedNow.toFixed(2)} is being charged to your card — a receipt is on its way by email.`
        : 'Flight logged. No charge for this flight — the Hobbs/Tach are recorded for the aircraft.'
    );
    setSubmitting(false);
    // Offer the fuel top-off text before leaving the page. Suppressed when the owner
    // logged this on someone else's behalf — they aren't the one standing at the plane.
    if (!adminFor) {
      setFuelPrompt(true);
      return;
    }
    goNext(hasSquawks ? 800 : 1200);
  }

  // Where logging sends you next. Delayed on the auto path so the result banner is
  // readable; immediate once the pilot has dismissed the fuel step themselves.
  function goNext(delay = 0) {
    const to = hasSquawks ? squawkRoute : flightsRoute;
    if (delay) setTimeout(() => nav(to), delay);
    else nav(to);
  }

  // ADMIN ONLY: pick whose flight this is. "Me" logs a free owner flight; picking
  // a lessee writes the flight to THEIR account and charges THEIR card on file —
  // for when someone flies and flakes on logging.
  const whoFlew = isAdmin && !demoMode && (
    <div className="card p-5 mb-6">
      <h2 className="font-display text-lg font-semibold text-navy-900">Who flew?</h2>
      <p className="mt-1 text-sm text-ink-muted">
        Logging for a lessee records the flight on their account and charges their card on
        file — exactly as if they’d logged it themselves.
      </p>
      <select className="input mt-3 max-w-sm" value={subjectUid} onChange={(e) => setSubjectUid(e.target.value)}>
        <option value="">Me — owner flight (not billed)</option>
        {[...lessees]
          .sort((a, b) => (a.personal?.firstName ?? a.email).localeCompare(b.personal?.firstName ?? b.email))
          .map((l) => (
          <option key={l.uid} value={l.uid}>
            {`${l.personal?.firstName ?? ''} ${l.personal?.lastName ?? ''}`.trim() || l.email}
            {l.status !== 'active' ? ` — ${l.status}` : ''}
          </option>
        ))}
      </select>
      {adminWarnings.map((w) => (
        <p key={w} className="mt-2 text-sm text-amber-700">⚠️ {w}</p>
      ))}
      {adminFor && adminWarnings.length === 0 && (
        <p className="mt-2 text-sm text-emerald-700">
          {isComped
            ? `✓ ${pilotFirst}’s flights are comped — this flight is recorded but nothing is charged.`
            : isDiscovery
            ? `✓ Discovery flight — ${guestName} is charged $${guestRate}/hr (fuel included). ${pilotFirst} is not charged.`
            : `✓ Billing as ${isPromo ? `promo ($${promoRate}/hr)` : isCfi ? 'CFI' : isOwner ? 'owner (not billed)' : `standard ($${ac?.hourlyRate.toFixed(0) ?? 100}/hr)`} — ${pilotFirst}${subject?.cardLast4 ? `’s card ending ${subject.cardLast4}` : `’s card on file`} will be charged on submit.`}
        </p>
      )}
    </div>
  );

  if (blockers.length > 0) {
    return (
      <>
        <PageHeader title="Log a flight" subtitle="Post-flight entry." />
        {whoFlew}
        <StatusBanner tone="danger" title="Flight logging is blocked">
          <ul className="list-disc pl-5">
            {blockers.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
        </StatusBanner>
        <div className="mt-6">
          <SharedLogbook demoMode={demoMode} />
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={adminFor ? `Log a flight for ${pilotFirst}` : 'Log a flight'}
        subtitle="Post-flight entry. Billing runs on submit."
      />

      {whoFlew}

      <form onSubmit={review} className="space-y-6">
          {/* STARTING READINGS — gates the end readings below. Pilots reach this
              after landing, not before takeoff, so the copy asks what the gauges
              read when they got in (past tense) rather than telling them to go
              check the panel. They never type the start; they confirm the
              auto-filled value or report what it actually read. */}
          <div className="card p-5">
            <h2 className="font-display text-lg font-semibold text-navy-900">Starting readings</h2>
            <p className="mt-1 text-sm text-ink-muted">
              {adminFor
                ? `Confirm the last logged reading matches where ${pilotFirst}’s flight started. If the master has since moved on (someone logged after them), choose “I started somewhere different” and enter ${pilotFirst}’s actual start.`
                : 'Confirm what the Hobbs and tach read when you got in. If they don’t match the last logged flight, someone flew without logging it — say so here and you won’t be billed for their time.'}
            </p>
            <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 sm:flex sm:flex-wrap sm:gap-8 rounded-lg bg-sky-50/60 border border-navy-900/5 px-4 py-3">
              <div>
                <div className="text-xs uppercase tracking-wider text-ink-muted">Last logged Hobbs</div>
                <div className="font-display text-xl font-semibold text-navy-900">{expectedHobbs.toFixed(1)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-ink-muted">Last logged tach</div>
                <div className="font-display text-xl font-semibold text-navy-900">{expectedTach.toFixed(1)}</div>
              </div>
              {lastSet && (
                <div className="col-span-2 border-t border-navy-900/5 pt-3 sm:border-0 sm:pt-0">
                  <div className="text-xs uppercase tracking-wider text-ink-muted">Logged</div>
                  <div className="font-display text-xl font-semibold text-navy-900">{fmtLogDay(lastSet.date, today)}</div>
                  <div className="text-xs text-ink-muted">by {lastSet.who}</div>
                </div>
              )}
            </div>

            {gaugeMatches === null && (
              <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  className="btn-primary flex-1"
                  onClick={() => {
                    setGaugeMatches(true);
                    setObservedHobbs('');
                    setObservedTach('');
                  }}
                >
                  ✓ That’s where I started — {expectedHobbs.toFixed(1)} / {expectedTach.toFixed(1)}
                </button>
                <button
                  type="button"
                  className="btn-secondary flex-1"
                  onClick={() => {
                    setGaugeMatches(false);
                    setObservedHobbs(expectedHobbs.toFixed(1));
                    setObservedTach(expectedTach.toFixed(1));
                  }}
                >
                  I started somewhere different
                </button>
              </div>
            )}

            {gaugeMatches === true && (
              <div className="mt-4 flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
                <span>Confirmed — starting at {expectedHobbs.toFixed(1)} Hobbs / {expectedTach.toFixed(1)} tach.</span>
                <button
                  type="button"
                  className="text-xs font-medium underline"
                  onClick={() => setGaugeMatches(null)}
                >
                  Change
                </button>
              </div>
            )}

            {gaugeMatches === false && (
              <div className="mt-4">
                <p className="text-sm text-amber-800">
                  Enter what the gauges actually read when you got in. You’ll be billed from these
                  numbers; the owner is notified to reconcile the difference.
                </p>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div>
                    <label className="label">Started at — Hobbs</label>
                    <input
                      type="number"
                      step="0.1"
                      className="input"
                      value={observedHobbs}
                      onChange={(e) => setObservedHobbs(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="label">Started at — tach</label>
                    <input
                      type="number"
                      step="0.1"
                      className="input"
                      value={observedTach}
                      onChange={(e) => setObservedTach(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <button
                  type="button"
                  className="mt-3 text-xs font-medium text-navy-700 underline"
                  onClick={() => setGaugeMatches(null)}
                >
                  Actually, it matched
                </button>
              </div>
            )}
          </div>

          <div className="card p-5">
            <h2 className="font-display text-lg font-semibold text-navy-900">Hobbs &amp; tach</h2>
            {gaugeMatches === null ? (
              <p className="mt-3 text-sm text-ink-muted">Confirm your starting readings above to log your end readings.</p>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
                  <div>
                    <label className="label">Hobbs start</label>
                    <input type="number" className="input bg-slate-50 text-ink-muted" value={hobbsOut} readOnly tabIndex={-1} />
                    <p className="mt-1 text-[11px] text-ink-subtle">
                      {gaugeMatches === false ? 'From your gauge reading' : 'From the aircraft — read-only'}
                    </p>
                  </div>
                  <div>
                    <label className="label">Hobbs end</label>
                    <input type="number" step="0.1" className="input" value={hobbsIn} onChange={(e) => setHobbsIn(e.target.value)} placeholder="Off the gauge" required />
                  </div>
                  <div>
                    <label className="label">Tach start</label>
                    <input type="number" className="input bg-slate-50 text-ink-muted" value={tachOut} readOnly tabIndex={-1} />
                    <p className="mt-1 text-[11px] text-ink-subtle">
                      {gaugeMatches === false ? 'From your gauge reading' : 'From the aircraft — read-only'}
                    </p>
                  </div>
                  <div>
                    <label className="label">Tach end</label>
                    <input type="number" step="0.1" className="input" value={tachIn} onChange={(e) => setTachIn(e.target.value)} placeholder="Off the gauge" required />
                  </div>
                </div>
                {checks.errors.map((er) => (
                  <p key={er} className="mt-2 text-sm font-medium text-red-700">{er}</p>
                ))}
                {checks.warnings.map((w) => (
                  <p key={w} className="mt-2 text-sm text-amber-700">⚠️ {w}</p>
                ))}
              </>
            )}
          </div>

          <div className="card p-5">
            <h2 className="font-display text-lg font-semibold text-navy-900">Flight details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="label">Date</label>
                <DateField value={date} onChange={(e) => setDate(e.target.value)} />
              </div>
              {/* 26 of the first 27 logged flights departed KLGB. Asking everyone to
                  confirm a prefilled box to catch the 27th is the wrong trade — it
                  defaults to KLGB and only appears when a pilot says otherwise. */}
              <div>
                <label className="label">Departed from</label>
                {showDeparture ? (
                  <input
                    className="input uppercase"
                    value={departureAirport}
                    onChange={(e) => setDepartureAirport(e.target.value)}
                    autoFocus
                    required
                  />
                ) : (
                  <div className="flex items-baseline gap-2">
                    <span className="font-display text-lg font-semibold text-navy-900">KLGB</span>
                    <button
                      type="button"
                      onClick={() => setShowDeparture(true)}
                      className="text-sm font-medium text-navy-700 underline"
                    >
                      Somewhere else?
                    </button>
                  </div>
                )}
              </div>
              <div className="sm:col-span-2">
                <label className="label">Where did you go?</label>
                <input
                  className="input uppercase"
                  placeholder="KCMA, KSBA, KLGB — leave blank if you stayed local"
                  value={destinations}
                  onChange={(e) => setDestinations(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="card p-5">
            <h2 className="font-display text-lg font-semibold text-navy-900">Flight summary</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-4">
              <div>
                <label className="label">Passengers</label>
                {discoveryGate ? (
                  <p className="input bg-slate-50 text-slate-600">0. The guest is your student</p>
                ) : (
                <select
                  className="input"
                  value={passengerCount}
                  onChange={(e) => setPassengerCount(Number(e.target.value))}
                >
                  {[0, 1, 2, 3].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                )}
              </div>
              <div>
                <label className="label">Nights away from KLGB</label>
                <input
                  type="number"
                  min={0}
                  className="input"
                  value={awayNights}
                  onChange={(e) => setAwayNights(Number(e.target.value))}
                />
                {/* Lease §5.3 counts midnights, not hours on the ground. Pilots who
                    got stuck somewhere all day read "away" as a day and type 1, which
                    charges them a minimum they do not owe. Say the rule at the field. */}
                <p className="mt-1.5 text-xs text-ink-muted">
                  Nights, not hours. Back at KLGB the same day is <strong>0</strong> — no
                  matter how long the airplane sat somewhere else.
                </p>
              </div>
            </div>
            <label className="mt-4 inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={hasSquawks}
                onChange={(e) => setHasSquawks(e.target.checked)}
              />
              I have squawks to report
            </label>
          </div>

          {billing && (
            <div className="card p-5">
              <h2 className="font-display text-lg font-semibold text-navy-900">
                {isOwner
                  ? 'Owner flight'
                  : isComped
                  ? 'Comped flight'
                  : isDiscovery
                  ? 'Discovery flight'
                  : isCfi
                  ? 'CFI flight'
                  : isPromo
                  ? 'Promo plan'
                  : adminFor
                  ? `What ${pilotFirst} will be charged`
                  : 'What you’ll be charged'}
              </h2>

              {isOwner && (
                <p className="mt-3 text-sm text-ink-muted">
                  This is an <strong>owner flight — not billed</strong>. The Hobbs/Tach are still recorded
                  so the aircraft’s time stays continuous across everyone who flies it.
                </p>
              )}

              {isComped && (
                <p className="mt-3 text-sm text-ink-muted">
                  {adminFor ? `${pilotFirst}’s flights are` : 'Your flights are'} <strong>comped — not
                  billed</strong>. The Hobbs/Tach are still recorded so the aircraft’s time stays
                  continuous across everyone who flies it.
                </p>
              )}

              {(isCfi || ownerTest) && discoveryBookings.length > 0 && (
                <div className="mt-3">
                  <label className="label">Discovery flight</label>
                  <select className="input" value={discoveryPick} onChange={(e) => setDiscoveryPick(e.target.value)}>
                    {discoveryBookings.map((b) => (
                      <option key={b.docId} value={b.docId}>
                        Yes, with {b.discoveryGuest || 'my guest'} ({b.startDateTime.toDate().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })})
                      </option>
                    ))}
                    <option value="none">No, this was my own flight</option>
                  </select>
                  {ownerTest && discoveryBooking && (
                    <p className="mt-1 text-xs text-ink-muted">Owner test: recorded, never billed. On a CFI’s discovery flight the guest’s card is charged here.</p>
                  )}
                </div>
              )}

              {isCfi && !isDiscovery && (
                <p className="mt-3 text-xs text-ink-muted">
                  This form is for your own flying. A checkout or lesson is the other pilot’s flight — they log it and pay for it.
                </p>
              )}

              {isDiscovery && discoveryBooking && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-4 text-sm">
                    <Stat label="Hours flown" value={`${billing.hoursFlown.toFixed(1)}`} />
                    <Stat label="Guest rate" value={`$${guestRate}/hr`} sub="fuel included" />
                    <Stat label={`Charged to ${guestName.split(' ')[0]}`} value={`$${(billing.hoursFlown * guestRate).toFixed(2)}`} accent />
                  </div>
                  <p className="text-xs text-ink-muted mt-3">
                    A <strong>discovery flight</strong> is billed to {guestName}
                    {discoveryBooking.guestCardLast4 ? `’s card ending ${discoveryBooking.guestCardLast4}` : ''}, by the Hobbs hour, fuel
                    included. <strong>You aren’t charged.</strong> Your own fee is between you and {guestName.split(' ')[0]}.
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                    <span className="font-medium text-navy-900">Did the airplane take off?</span>
                    <label className="flex items-center gap-1.5"><input type="radio" name="tookOff" checked={tookOff === 'yes'} onChange={() => setTookOff('yes')} /> Yes</label>
                    <label className="flex items-center gap-1.5"><input type="radio" name="tookOff" checked={tookOff === 'no'} onChange={() => setTookOff('no')} /> No, don’t charge the guest</label>
                  </div>
                </>
              )}

              {isPromo && !isDiscovery && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4 text-sm">
                    <Stat label="Hours flown" value={`${billing.hoursFlown.toFixed(1)}`} />
                    <Stat label="Rate" value={`$${promoRate}/hr`} />
                    <Stat label="Away-from-base" value={`$${billing.awayFromBaseCharge.toFixed(2)}`} />
                    <Stat label="Charged now" value={`$${chargedNow.toFixed(2)}`} accent />
                  </div>
                  <p className="text-xs text-ink-muted mt-3">
                    {adminFor ? `${pilotFirst} is` : 'You’re'} on the <strong>promo plan</strong> — ${promoRate}/Hobbs-hr,
                    no monthly minimum. {adminFor ? 'They’re' : 'You’re'} billed only for the hours flown.
                  </p>
                </>
              )}

              {!isOwner && !isComped && !isCfi && !isPromo && est && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4 text-sm">
                    <Stat label="Hours flown" value={`${billing.hoursFlown.toFixed(1)}`} />
                    <Stat
                      label="This flight"
                      value={`$${est.hoursCharge.toFixed(2)}`}
                      sub={`${est.hoursFlown.toFixed(1)} hr × $${ac?.hourlyRate.toFixed(0)}/hr`}
                    />
                    <Stat
                      label="Away-from-base"
                      value={`$${est.awayCharge.toFixed(2)}`}
                      sub={billing.awayNights ? `${billing.awayNights} night(s)` : '—'}
                    />
                    <Stat
                      label="Charged now"
                      value={`$${chargedNow.toFixed(2)}`}
                      accent
                    />
                  </div>
                  <p className="text-xs text-ink-muted mt-3">
                    No monthly minimum — {adminFor ? `${pilotFirst} is` : 'you’re'} billed ${ac?.hourlyRate.toFixed(0) ?? 100}/hr
                    for the Hobbs hours flown, plus any away-from-base, right after the flight is logged.
                    Final amount is reconciled and charged by AJM’s billing system.
                  </p>
                </>
              )}
            </div>
          )}

          {result && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-900 px-3 py-2 text-sm">
              {result}
            </div>
          )}

          <div className="flex justify-end gap-2 sticky bottom-0 bg-sky-50/95 backdrop-blur py-3 -mx-4 sm:mx-0 px-4 sm:px-0 border-t border-navy-900/10">
            <button type="button" className="btn-secondary" onClick={() => nav(-1)}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={!billing || checks.errors.length > 0 || !gaugeReady || submitting}
              className="btn-primary"
            >
              {charge.amount > 0 ? 'Review & charge' : 'Review & log'}
            </button>
          </div>
        </form>

      <div className="mt-6">
        <SharedLogbook demoMode={demoMode} />
      </div>

      {confirming && billing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <ModalClose onClose={() => setConfirming(false)} />
            <h2 className="pr-10 font-display text-xl font-semibold text-navy-900">Double-check your readings</h2>
            <p className="mt-1 text-sm text-ink-muted">
              {chargedNow > 0
                ? payCash
                  ? 'No card will be charged — this is recorded as cash owed. Confirm the numbers are right; there’s no undo.'
                  : adminFor
                  ? `This charges ${pilotFirst}’s card on file. Confirm the numbers are right — there’s no undo.`
                  : 'This charges the card on file. Confirm the numbers are right — there’s no undo.'
                : 'Confirm the numbers are right before you log this flight.'}
            </p>
            <dl className="mt-4 divide-y divide-slate-100 text-sm">
              <ConfirmRow k="Hobbs" v={`${hobbsOut} → ${hobbsIn}`} />
              <ConfirmRow k="Tach" v={`${tachOut} → ${tachIn}`} />
              <ConfirmRow k="Hours flown" v={billing.hoursFlown.toFixed(1)} />
            </dl>
            {checks.warnings.length > 0 && (
              <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                {checks.warnings.map((w) => (
                  <div key={w}>⚠️ {w}</div>
                ))}
              </div>
            )}
            {chargedNow > 0 && (
              <div className="mt-4">
                <div className="label mb-1.5">How are you paying?</div>
                <div className="grid grid-cols-2 gap-2">
                  {([
                    { v: false, t: 'Card on file', s: 'Charged now, automatically' },
                    { v: true, t: 'Cash', s: `You settle up with ${OWNER_FIRST}` },
                  ] as const).map((o) => (
                    <button
                      key={o.t}
                      type="button"
                      onClick={() => setPayCash(o.v)}
                      className={`rounded-xl border px-3 py-2.5 text-left transition ${
                        payCash === o.v
                          ? 'border-brand bg-brand text-white'
                          : 'border-slate-200 bg-white text-navy-900 hover:border-navy-900/40'
                      }`}
                    >
                      <div className="text-sm font-semibold">{o.t}</div>
                      <div className={`text-[11px] leading-snug ${payCash === o.v ? 'text-sky-100' : 'text-ink-muted'}`}>
                        {o.s}
                      </div>
                    </button>
                  ))}
                </div>
                {payCash && (
                  <p className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs leading-snug text-amber-900">
                    Your card won’t be charged. You’ll pay {OWNER_FIRST} directly.
                  </p>
                )}
              </div>
            )}
            <div className="mt-4 rounded-xl bg-brand px-4 py-3 text-center text-white">
              <div className="text-xs uppercase tracking-wider text-sky-100">
                {isOwner
                  ? 'Owner flight — not billed'
                  : isDiscovery
                  ? tookOff === 'no'
                    ? `No takeoff — ${guestName.split(' ')[0]} isn’t charged`
                    : `${guestName.split(' ')[0]}’s card will be charged · you aren’t`
                  : charge.mode === 'comped'
                  ? 'Comped — not billed'
                  : isPromo
                  ? payCash
                    ? `Cash owed to ${OWNER_FIRST}`
                    : 'Card will be charged'
                  : chargedNow > 0
                  ? payCash
                    ? `Cash owed to ${OWNER_FIRST}`
                    : 'Card will be charged'
                  : 'No charge for this flight'}
              </div>
              <div className="font-display text-3xl font-bold">
                ${(isDiscovery ? (tookOff === 'no' ? 0 : billing.hoursFlown * guestRate) : chargedNow).toFixed(2)}
              </div>
            </div>
            <div className="mt-5 flex gap-2">
              <button
                type="button"
                className="btn-secondary flex-1"
                onClick={() => setConfirming(false)}
                disabled={submitting}
              >
                Go back &amp; edit
              </button>
              <button type="button" className="btn-primary flex-1" onClick={doSubmit} disabled={submitting}>
                {submitting
                  ? 'Logging…'
                  : charge.amount > 0
                  ? payCash
                    ? `Confirm — $${charge.amount.toFixed(2)} cash`
                    : `Confirm & charge $${charge.amount.toFixed(2)}`
                  : 'Confirm & log flight'}
              </button>
            </div>
          </div>
        </div>
      )}

      {fuelPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="font-display text-xl font-semibold text-navy-900">
              Top off {tail} at {FUEL_FBO}
            </h2>
            <p className="mt-1 text-sm text-ink-muted">
              Flight logged. Park at {FUEL_PARKING} and leave {tail} with full tanks.
            </p>
            <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-3">
              <Fuel size={15} className="mt-0.5 shrink-0 text-amber-700" aria-hidden="true" />
              <div className="space-y-1.5 text-[13px] leading-snug text-amber-900">
                <p>
                  <span className="font-semibold">Fuel at {FUEL_FBO}.</span> Fueling elsewhere en
                  route is fine — the last top-off happens here.
                </p>
                {isDiscovery ? (
                  <p>
                    <span className="font-semibold">Bill the card on file.</span> Discovery flights include
                    fuel, so the aircraft&rsquo;s card pays for this top-off. Send the text below.
                  </p>
                ) : isOwner ? (
                  <p>
                    <span className="font-semibold">Bill the card on file.</span> Your own flight, so
                    the aircraft&rsquo;s card is the right one — which is what the text below asks for.
                  </p>
                ) : (
                  <p>
                    <span className="font-semibold">Use your own card.</span> Fuel is at your expense.
                    Confirm {FUEL_FBO} is billing you and not the aircraft.
                  </p>
                )}
              </div>
            </div>
            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-navy-900">
              {fuelMessage}
            </div>
            {demoMode && (
              <p className="mt-2 text-xs text-ink-muted">
                Demo mode — this opens Messages with no recipient, so there’s nothing to send. In the
                real app it’s addressed to {FUEL_FBO}.
              </p>
            )}
            {fuelAck ? (
              <div className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-900">
                Thanks — nothing else needed.
              </div>
            ) : (
              <div className="mt-5 space-y-2">
                <div className="flex gap-2">
                  <a
                    className="btn-secondary flex-1 text-center"
                    href={demoMode ? undefined : buildFuelCallHref()}
                    onClick={() => setFuelTapped(true)}
                  >
                    Call for fuel
                  </a>
                  <a className="btn-primary flex-1 text-center" href={fuelHref} onClick={() => setFuelTapped(true)}>
                    Text for fuel
                  </a>
                </div>
                <button
                  type="button"
                  className="btn-secondary w-full"
                  onClick={() => {
                    setFuelAck(true);
                    setTimeout(() => {
                      setFuelPrompt(false);
                      goNext();
                    }, 1100);
                  }}
                >
                  {fuelTapped ? 'Done' : 'Already full — no fuel needed'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// One-shot per session, admin only: reconcile the shared logbook against the
// private flights collection. Flights logged before the shared logbook existed
// (or whose best-effort logbook write failed) get their entry created here, so
// the full history shows for every pilot. Idempotent — dedupes on flightId.
let logbookBackfillRan = false;
async function backfillSharedLogbook() {
  if (logbookBackfillRan || !db) return;
  logbookBackfillRan = true;
  try {
    const [flightsSnap, logSnap, usersSnap] = await Promise.all([
      getDocs(collection(db, 'flights')),
      getDocs(collection(db, 'flightLog')),
      getDocs(collection(db, 'users')),
    ]);
    const logged = new Set(logSnap.docs.map((d) => (d.data() as FlightLogDoc).flightId));
    const nameByUid: Record<string, string> = {};
    usersSnap.docs.forEach((d) => {
      const u = d.data() as UserDoc;
      nameByUid[u.uid] =
        `${u.personal?.firstName ?? ''} ${u.personal?.lastName ?? ''}`.trim() || u.email || 'Pilot';
    });
    for (const d of flightsSnap.docs) {
      const f = d.data() as FlightDoc;
      if (!f.flightId || logged.has(f.flightId)) continue;
      await addDoc(collection(db, 'flightLog'), {
        flightId: f.flightId,
        aircraftId: f.aircraftId,
        pilotUid: f.lesseeUid,
        pilotName: nameByUid[f.lesseeUid] ?? 'Pilot',
        date: f.date,
        hobbsOut: f.hobbsOut,
        hobbsIn: f.hobbsIn,
        tachOut: f.tachOut ?? 0,
        tachIn: f.tachIn ?? 0,
        hoursFlown: f.hoursFlown,
        departureAirport: f.departureAirport ?? '',
        destinations: f.destinations ?? [],
        // Original timestamp so backfilled entries sort into their true position.
        createdAt: f.createdAt ?? Timestamp.now(),
      });
    }
  } catch {
    logbookBackfillRan = false; // transient failure — retry on next visit
  }
}

// Shared, non-financial aircraft logbook — every pilot sees recent flights here.
function SharedLogbook({ demoMode }: { demoMode: boolean }) {
  const { isAdmin } = useAuth();
  const [logs, setLogs] = useState<FlightLogDoc[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (demoMode) {
      const nameByUid: Record<string, string> = {};
      DEMO_LESSEES.forEach((l) => {
        nameByUid[l.uid] = `${l.personal?.firstName ?? ''} ${l.personal?.lastName ?? ''}`.trim() || 'Pilot';
      });
      const rows = [...DEMO_FLIGHTS]
        .sort((a, b) => (a.date < b.date ? 1 : -1))
        .slice(0, 12)
        .map((f, i) => ({
          logId: `demo-${i}`,
          flightId: f.flightId,
          aircraftId: f.aircraftId,
          pilotUid: f.lesseeUid,
          pilotName: nameByUid[f.lesseeUid] || 'Pilot',
          date: f.date,
          hobbsOut: f.hobbsOut,
          hobbsIn: f.hobbsIn,
          tachOut: (f as { tachOut?: number }).tachOut ?? 0,
          tachIn: (f as { tachIn?: number }).tachIn ?? 0,
          hoursFlown: f.hoursFlown,
          departureAirport: f.departureAirport,
          destinations: f.destinations,
        })) as unknown as FlightLogDoc[];
      setLogs(rows);
      return;
    }
    if (!db) return;
    if (isAdmin) void backfillSharedLogbook();
    const unsub = onSnapshot(
      query(collection(db, 'flightLog'), orderBy('createdAt', 'desc'), limit(showAll ? 500 : 20)),
      (snap) => setLogs(snap.docs.map((d) => ({ ...(d.data() as FlightLogDoc), logId: d.id })))
    );
    return () => unsub();
  }, [demoMode, isAdmin, showAll]);

  return (
    <div className="card p-5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="-m-1 flex w-full items-center justify-between gap-3 rounded-lg p-1 text-left"
      >
        <div>
          <h2 className="font-display text-lg font-semibold text-navy-900">Aircraft logbook</h2>
          <span className="text-xs text-ink-muted">
            {open
              ? showAll
                ? 'All flights · everyone'
                : 'Recent flights · everyone'
              : 'Every pilot’s recent flights — tap to open'}
          </span>
        </div>
        <ChevronDown
          size={20}
          className={`shrink-0 text-navy-700 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {!open ? null : logs.length === 0 ? (
        <p className="mt-3 text-sm text-ink-muted">
          No flights logged yet. The first flight anyone logs shows up here for all pilots.
        </p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-ink-muted">
                <th className="py-2 pr-3 font-medium">Date</th>
                <th className="py-2 pr-3 font-medium">Pilot</th>
                <th className="py-2 pr-3 font-medium">Route</th>
                <th className="py-2 pr-3 text-right font-medium">Hobbs</th>
                <th className="py-2 pr-3 text-right font-medium">Tach</th>
                <th className="py-2 pl-3 text-right font-medium">Hrs</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {logs.map((l) => (
                <tr key={l.logId}>
                  <td className="py-2 pr-3 whitespace-nowrap text-ink-muted">{l.date}</td>
                  <td className="py-2 pr-3 whitespace-nowrap font-medium text-navy-900">{l.pilotName}</td>
                  <td className="py-2 pr-3 whitespace-nowrap text-ink">
                    {l.departureAirport}
                    {l.destinations?.length ? ` → ${l.destinations.join(' → ')}` : ''}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums text-ink whitespace-nowrap">
                    {Number(l.hobbsOut).toFixed(1)} → {Number(l.hobbsIn).toFixed(1)}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums text-ink-muted whitespace-nowrap">
                    {Number(l.tachOut).toFixed(1)} → {Number(l.tachIn).toFixed(1)}
                  </td>
                  <td className="py-2 pl-3 text-right tabular-nums font-semibold text-navy-900">
                    {Number(l.hoursFlown).toFixed(1)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-2 flex items-center justify-between">
            <span className="text-xs text-ink-muted">
              {logs.length} flight{logs.length === 1 ? '' : 's'} ·{' '}
              {logs.reduce((s, l) => s + Number(l.hoursFlown || 0), 0).toFixed(1)} Hobbs hrs shown
            </span>
            {(showAll || logs.length >= 20) && (
              <button
                type="button"
                className="text-xs font-medium text-navy-700 underline"
                onClick={() => setShowAll((v) => !v)}
              >
                {showAll ? 'Show recent only' : 'Show full history'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Check({ ok, children }: { ok?: boolean; children: React.ReactNode }) {
  return (
    <li className={`flex items-center gap-2 ${ok ? 'text-ink' : 'text-red-700'}`}>
      <span
        className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-bold ${
          ok ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
        }`}
      >
        {ok ? '✓' : '!'}
      </span>
      {children}
    </li>
  );
}

function ConfirmRow({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between py-2">
      <dt className="text-ink-muted">{k}</dt>
      <dd className="font-semibold text-navy-900">{v}</dd>
    </div>
  );
}

function Stat({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: boolean }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-ink-muted">{label}</div>
      <div className={`text-lg font-display font-semibold ${accent ? 'text-emerald-700' : 'text-navy-900'}`}>
        {value}
      </div>
      {sub && <div className="text-xs text-ink-muted">{sub}</div>}
    </div>
  );
}

// YYYY-MM-DD in the viewer's local day for an ISO timestamp.
function localDay(isoTs: string): string {
  const d = new Date(isoTs);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// "Today" / "Yesterday" / "Tue, Oct 6 · 9 days ago" for a YYYY-MM-DD flight date.
function fmtLogDay(ymd: string, todayYmd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const [ty, tm, td] = todayYmd.split('-').map(Number);
  if (!y || !m || !d) return ymd;
  const days = Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(y, m - 1, d)) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  const label = new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  return days > 1 ? `${label} · ${days} days ago` : label;
}
